// ============================================================================
// API CLIENT
// ============================================================================

const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api'

// Types
export interface User {
  id: string
  email: string
  name: string | null
  is_active: boolean
  is_admin: boolean
  created_at: string
  updated_at: string
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface ApiError {
  detail: string
}

// Token management
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('access_token')
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('refresh_token')
}

export function setTokens(tokens: TokenResponse): void {
  localStorage.setItem('access_token', tokens.access_token)
  localStorage.setItem('refresh_token', tokens.refresh_token)
}

export function clearTokens(): void {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

// API request helper
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_URL}${endpoint}`
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Add auth header if token exists
  const token = getAccessToken()
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // Handle 401 - try to refresh token
  if (response.status === 401 && getRefreshToken()) {
    const refreshed = await refreshTokens()
    if (refreshed) {
      // Retry with new token
      (headers as Record<string, string>)['Authorization'] = `Bearer ${getAccessToken()}`
      const retryResponse = await fetch(url, { ...options, headers })
      if (!retryResponse.ok) {
        const error = await retryResponse.json().catch(() => ({ detail: 'Request failed' }))
        throw new Error(error.detail || 'Request failed')
      }
      return retryResponse.json()
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }))
    throw new Error(error.detail || 'Request failed')
  }

  return response.json()
}

// Refresh tokens
async function refreshTokens(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      clearTokens()
      return false
    }

    const tokens: TokenResponse = await response.json()
    setTokens(tokens)
    return true
  } catch {
    clearTokens()
    return false
  }
}

// ============================================================================
// AUTH API
// ============================================================================

export async function register(data: {
  email: string
  password: string
  name?: string
}): Promise<User> {
  return request<User>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function login(email: string, password: string): Promise<TokenResponse> {
  const response = await fetch(`${API_URL}/auth/login/json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Login failed' }))
    throw new Error(error.detail || 'Login failed')
  }

  const tokens: TokenResponse = await response.json()
  setTokens(tokens)
  return tokens
}

export async function logout(): Promise<void> {
  clearTokens()
}

export async function getCurrentUser(): Promise<User> {
  return request<User>('/auth/me')
}

export async function updateProfile(data: {
  name?: string
  email?: string
}): Promise<User> {
  return request<User>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function changePassword(data: {
  current_password: string
  new_password: string
}): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/me/password', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ============================================================================
// SHOP TYPES
// ============================================================================

export interface Category {
  id: string
  name: string
  slug: string
  description: string | null
}

export interface ProductImage {
  id: string
  url: string
  position: number
}

export interface Variant {
  id: string
  size: string | null
  stock: number
}

export interface ProductListItem {
  id: string
  title: string
  slug: string
  price: number
  condition: string
  is_active: boolean
  category: Category | null
  images: ProductImage[]
  total_stock: number
  is_sold_out: boolean
}

export interface ProductDetail extends ProductListItem {
  description: string
  variants: Variant[]
  created_at: string
  updated_at: string
}

export interface AdminProduct extends ProductListItem {
  variants: Variant[]
  total_sold: number
  created_at: string
  updated_at: string
}

export interface OrderItem {
  id: string
  product_id: string | null
  variant_id: string | null
  product_title: string
  size: string | null
  unit_price: number
  quantity: number
  image_url: string | null
}

export interface Order {
  id: string
  order_number: string
  user_id: string
  status: string
  total_amount: number
  email: string
  full_name: string | null
  address: string | null
  postal_code: string | null
  city: string | null
  items: OrderItem[]
  created_at: string
  updated_at: string
}

export interface OrderSummary {
  id: string
  order_number: string
  status: string
  total_amount: number
  created_at: string
  item_count: number
}

export interface CheckoutResult {
  order: Order
  payment_url: string | null
  requires_payment: boolean
}

export interface SalesStats {
  total_orders: number
  paid_orders: number
  total_revenue: number
  total_items_sold: number
  total_products: number
  active_products: number
  sold_out_products: number
  low_stock_products: number
}

export interface VariantInput {
  size: string | null
  stock: number
}

export interface ProductPayload {
  title: string
  description: string
  price: number
  condition: string
  category_id: string | null
  is_active: boolean
  image_urls: string[]
  variants: VariantInput[]
}

// ============================================================================
// SHOP API (public)
// ============================================================================

export async function getCategories(): Promise<Category[]> {
  return request<Category[]>('/categories')
}

export async function getProducts(params: {
  category?: string
  search?: string
} = {}): Promise<ProductListItem[]> {
  const query = new URLSearchParams()
  if (params.category) query.set('category', params.category)
  if (params.search) query.set('search', params.search)
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return request<ProductListItem[]>(`/products${suffix}`)
}

export async function getProduct(slug: string): Promise<ProductDetail> {
  return request<ProductDetail>(`/products/${slug}`)
}

// ============================================================================
// ORDERS API (authenticated)
// ============================================================================

export interface CheckoutPayload {
  items: { variant_id: string; quantity: number }[]
  email?: string
  full_name?: string
  address?: string
  postal_code?: string
  city?: string
}

export async function checkout(payload: CheckoutPayload): Promise<CheckoutResult> {
  return request<CheckoutResult>('/orders/checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getMyOrders(): Promise<OrderSummary[]> {
  return request<OrderSummary[]>('/orders')
}

export async function getOrder(orderId: string): Promise<Order> {
  return request<Order>(`/orders/${orderId}`)
}

export async function confirmPayment(orderId: string): Promise<Order> {
  return request<Order>(`/orders/${orderId}/confirm-payment`, {
    method: 'POST',
  })
}

// ============================================================================
// ADMIN API
// ============================================================================

export async function adminGetProducts(): Promise<AdminProduct[]> {
  return request<AdminProduct[]>('/admin/products')
}

export async function adminGetOrders(): Promise<Order[]> {
  return request<Order[]>('/admin/orders')
}

export async function adminGetStats(): Promise<SalesStats> {
  return request<SalesStats>('/admin/stats')
}

export async function adminGetProduct(productId: string): Promise<ProductDetail> {
  return request<ProductDetail>(`/products/id/${productId}`)
}

export async function createProduct(payload: ProductPayload): Promise<ProductDetail> {
  return request<ProductDetail>('/products', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateProduct(
  productId: string,
  payload: Partial<ProductPayload>
): Promise<ProductDetail> {
  return request<ProductDetail>(`/products/id/${productId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function deleteProduct(productId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/products/id/${productId}`, {
    method: 'DELETE',
  })
}

export async function setVariantStock(
  variantId: string,
  stock: number
): Promise<Variant> {
  return request<Variant>(`/products/variants/${variantId}/stock`, {
    method: 'PATCH',
    body: JSON.stringify({ stock }),
  })
}

export async function createCategory(data: {
  name: string
  description?: string
}): Promise<Category> {
  return request<Category>('/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function deleteCategory(categoryId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/categories/${categoryId}`, {
    method: 'DELETE',
  })
}

export async function uploadImage(file: File): Promise<{ url: string; filename: string }> {
  const token = getAccessToken()
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${API_URL}/upload/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Uppladdning misslyckades' }))
    throw new Error(error.detail || 'Uppladdning misslyckades')
  }

  return response.json()
}
